/**
 * @typedef { import('./reactive-controller.js').ReactiveController } ReactiveController
 * @typedef { import('./reactive-controller.js').ReactiveControllerHost } ReactiveControllerHost
 */

const FOCUSABLE =
  '[tabindex],a,button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled])';

/**
 * Element event binding `ReactiveController`.
 * Automatically adds/removes event bindings when host element connected/disconnected.
 * @implements ReactiveController
 */
export class AutofocusController {
  /**
   * Constructor
   * @param { ReactiveControllerHost & HTMLElement } host
   * @param { { shouldAutofocus: () => boolean } } options
   */
  constructor(host, options) {
    (this._host = host).addController(this);
    this._shouldAutofocus = options.shouldAutofocus;
  }

  hostUpdated() {
    const connected = this._host.isConnected;
    const visible = !this._host.hidden;

    if (connected && visible && this._shouldAutofocus()) {
      // Delay one task tick to sync with update and render
      Promise.resolve().then(() => {
        getFirstFocusableElement(this._host).focus({
          preventScroll: true,
        });
      });
    }
  }
}

/**
 * Returns the first focusable child of "element" (or itself if no matching children).
 * @param { HTMLElement } element
 * @returns { HTMLElement }
 */
function getFirstFocusableElement(element) {
  if (document.activeElement && element.contains(document.activeElement)) {
    return /** @type { HTMLElement } */ (document.activeElement);
  }

  const autofocus = element.querySelector('[autofocus]');

  if (autofocus) {
    return /** @type { HTMLElement } */ (autofocus);
  }

  const focusable = element.querySelectorAll(FOCUSABLE);

  if (focusable.length) {
    return /** @type { HTMLElement } */ (focusable[0]);
  }

  return element;
}
